import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,

} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ScaleIcon from "@mui/icons-material/Scale";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BackspaceIcon from "@mui/icons-material/Backspace";
import Keypad from "./Keypad";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import Webcam from "react-webcam";
import { SALENG_SHOP_API_PROD } from "../config";
import { getAuthorizationHeaders } from "../services/apiClient";
import { formatNumber } from "../utils/formatNumber";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PriceSelectionDialog from "./PriceSelectionDialog";
import { useProductsBranch } from "../hooks/useProductsBranch";
import ProductSelectDialog from "./ProductSelectDialog";
import { hasPermission } from "../auth/RequirePermission3";

const SALENG_SHOP_API = SALENG_SHOP_API_PROD;
const beep = new Audio("/sounds/beep.mp3");

const SimpleDialog = (props) => {
  const theme = useTheme();
  const {
    open,
    onClose,
    onSubmit,
    selectedValue,
    transaction,
    stockByProductId = {},
    waitingSaleNetByProductId = {},
  } = props;

  const [weight, setWeight] = useState(0);
  const [deduct, setDeduct] = useState(0);
  const [focusedImpurityIndex, setFocusedImpurityIndex] = useState(null);
  const [focusedImpurityField, setFocusedImpurityField] = useState(null);
  const [focusedImpuritySource, setFocusedImpuritySource] = useState(null);
  const [productPrice, setProductPrice] = useState();
  const [productStock, setProductStock] = useState();
  const [priceInput, setPriceInput] = useState("");
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [mute, setMute] = useState(true);
  const [focused, setFocused] = useState("weight");
  const [isDisabled, setIsDisabled] = useState(true);
  const [openPriceDialog, setOpenPriceDialog] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [isFirstKeypress, setIsFirstKeypress] = useState(true);
  const [inputValue, setInputValue] = useState(0);
  const lastWeightRef = useRef(null);
  const stableStartTimeRef = useRef(null);
  const beepPlayedRef = useRef(false);

  const [openProdDialog, setOpenProdDialog] = useState(false);
  const [catFilter, setCatFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const isCapturingRef = useRef(false);

  const [impurities, setImpurities] = useState([
    { kind: "", type: "", weight: 0 },
  ]);
  const [deductedItems, setDeductedItems] = useState([{ type: "", weight: 0 }]);

  const [isLockedPriceState, setIsLockedPriceState] = useState(
    selectedValue?.value?.isLockedPrice ?? false
  );
  const latestFinalPriceRef = useRef(
    Number(
      selectedValue?.finalPrice ??
        selectedValue?.pricePerUnit ??
        selectedValue?.value?.finalPrice ??
        selectedValue?.value?.pricePerUnit ??
        0
    ) || 0
  );

  const syncFinalPrice = (nextPrice) => {
    const normalized = Number(nextPrice ?? 0) || 0;
    latestFinalPriceRef.current = normalized;
    setProductPrice((prev) => ({ ...(prev || {}), finalPrice: normalized }));
  };

  const deductKindOptions = [
    { value: "convert", label: "หักเป็นสินค้าอื่น" },
    { value: "discard", label: "หักทิ้ง" },
    { value: "return", label: "หักคืน" },
  ];

  const impurityOptions = [
    "ปุ๋ย",
    "เต๋า",
    "ลัง",
    "ขยะฝุ่นดิน",
    "ลวดรัด",
    "ถุงพลาสติก",
    "สินค้าไม่รับซื้อ",
    "กระบะ",
    "เหล็กทิ้ง",
    "น้ำ",
    "พลาสติก",
    "สติ๊กเกอร์",
    "พาเลท",
    "ความชื้น",
    "เชือก",
    "น้ำมัน",
    "เปอร์เซ็นต์",
    "สี",
    "ปูน",
    "ไม้",
    "ตระกร้า",
  ];

  const TYPE_WHITELIST = {
    discard: [
      "ปุ๋ย",
      "เต๋า",
      "ลัง",
      "ขยะฝุ่นดิน",
      "ลวดรัด",
      "ถุงพลาสติก",
      "สินค้าไม่รับซื้อ",
      "กระบะ",
      "เหล็กทิ้ง",
      "น้ำ",
      "พลาสติก",
      "สติ๊กเกอร์",
      "พาเลท",
      "ความชื้น",
      "เชือก",
      "น้ำมัน",
      "เปอร์เซ็นต์",
      "สี",
      "ปูน",
      "ไม้",
      "ตระกร้า",
    ],
    return: [
      "ปุ๋ย",
      "เต๋า",
      "ลัง",
      "ขยะฝุ่นดิน",
      "ลวดรัด",
      "ถุงพลาสติก",
      "สินค้าไม่รับซื้อ",
      "กระบะ",
      "เหล็กทิ้ง",
      "น้ำ",
      "พลาสติก",
      "สติ๊กเกอร์",
      "พาเลท",
      "ความชื้น",
      "เชือก",
      "น้ำมัน",
      "เปอร์เซ็นต์",
      "สี",
      "ปูน",
      "ไม้",
      "ตระกร้า",
    ],
    convert: ["__deduct_product__"],
  };

  const normalize = (s = "") => s.replace(/\s|\+/g, "");

  const getTypeOptionsByKind = (kind) => {
    if (!kind) return [];
    if (kind === "convert") return ["__deduct_product__"];
    const allow = TYPE_WHITELIST[kind] || [];
    const allowNorm = allow.map(normalize);
    return impurityOptions.filter((opt) =>
      allowNorm.some((a) => normalize(opt).includes(a))
    );
  };

  const inferKindFromType = (type) => {
    if (!type) return "";
    // กรณีพิเศษ: หักเป็นสินค้าอื่น
    if (type === "__deduct_product__") return "convert";

    const t = normalize(type);
    for (const [kind, arr] of Object.entries(TYPE_WHITELIST)) {
      const allowNorm = arr.map(normalize);
      // ใช้ทั้ง equal และ includes เผื่อชื่อ type มีคำขยาย
      if (allowNorm.some((a) => a === t || t.includes(a))) {
        return kind;
      }
    }
    return "";
  };

  const webcamRef = useRef(null);
  const weightInputRef = useRef(null);
  const impurityWeightRefs = useRef({});
  const impurityTypeRefs = useRef({});
  const priceInputRef = useRef(null);

  const focusImpurityTypeAt = (source, index) => {
    const key = `${source}-${index}`;
    const el = impurityTypeRefs.current[key];
    el?.focus?.();
  };

  const { products } = useProductsBranch();
  const [openMainProdDialog, setOpenMainProdDialog] = useState(false);
  const [mainProduct, setMainProduct] = useState(null);
  const [prodPickerIndex, setProdPickerIndex] = useState(null);
  const [prodPickerSource, setProdPickerSource] = useState(null);
  const [note, setNote] = useState("");

  const [isEditingNote, setIsEditingNote] = useState(false);

  const categories = useMemo(() => {
    const seen = new Set();
    return (products || [])
      .map((p) => p.category)
      .filter((cat) => {
        if (!cat) return false;
        if (seen.has(cat.name)) return false;
        seen.add(cat.name);
        return true;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [products]);

  const focusMainWeight = useCallback(() => {
    const el = weightInputRef.current;
    if (el) {
      el.focus();
      if (typeof el.select === "function") el.select();
    }
    setIsEditingNote(false);
    setFocusedImpurityIndex(null);
    setFocusedImpurityField(null);
    setFocusedImpuritySource(null);
    setInputValue(weight || 0);
  }, [weight]);

  const focusImpurityWeightAt = useCallback(
    (source, index) => {
      const key = `${source}-${index}`;
      const el = impurityWeightRefs.current[key];
      const weightVal =
        source === "impurities"
          ? (impurities[index]?.weight ?? 0)
          : (deductedItems[index - impurities.length]?.weight2 ?? 0);
      if (el) {
        el.focus({ preventScroll: true });
        queueMicrotask(() => {
          try {
            const len = String(el.value ?? "").length;
            el.setSelectionRange?.(len, len);
          } catch { }
        });
      }
      setIsEditingNote(false);
      setFocusedImpurityIndex(index);
      setFocusedImpurityField("weight");
      setFocusedImpuritySource(source);
      setInputValue(weightVal || 0);
    },
    [impurities, deductedItems]
  );

  useEffect(() => {

    setProductStock(selectedValue.stock);

    const initialPrice =
      Number(
        selectedValue?.finalPrice ??
          selectedValue?.pricePerUnit ??
          selectedValue?.value?.finalPrice ??
          selectedValue?.value?.pricePerUnit ??
          0
      ) || 0;
    latestFinalPriceRef.current = initialPrice;
    setProductPrice({ ...(selectedValue || {}), finalPrice: initialPrice });
  }, [selectedValue]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devs) => {
      const videoInputs = devs.filter((d) => d.kind === "videoinput");
      setDevices(videoInputs);
      if (videoInputs.length > 0) setSelectedDeviceId(videoInputs[0].deviceId);
    });
    if (selectedValue?.id && selectedValue?.title) {
      setMainProduct({
        id: selectedValue.id,
        title: selectedValue.title,
        categoryId: selectedValue.categoryId,
      });
    } else {
      setMainProduct(null);
    }
    setNote(selectedValue?.note ?? "");
    setIsLockedPriceState(selectedValue?.value?.isLockedPrice ?? false);
  }, [selectedValue]);

  useEffect(() => {
    if (selectedValue) {
      const {
        weight: w = 0,
        deduct: d = 0,
        impurities: imps = [],
        deductedItems: deds = [],
      } = selectedValue;
      setWeight(w);
      setDeduct(d);
      setImpurities(
        (imps || []).map((it) => {
          const type = it.type ?? "";
          const kind = (it.kind ?? "").trim() || inferKindFromType(type); // เติม kind ถ้าว่าง
          return {
            kind,
            type,
            ...it,
          };
        })
      );
      setDeductedItems(deds);
    }

    const socket = new WebSocket("ws://localhost:3001/ws");
    socket.onmessage = handleData;

    const interval = setInterval(() => {
      if (
        lastWeightRef.current !== null &&
        stableStartTimeRef.current !== null
      ) {
        const elapsed = Date.now() - stableStartTimeRef.current;
        if (
          elapsed >= 2500 &&
          !beepPlayedRef.current &&
          lastWeightRef.current !== 0 &&
          !mute
        ) {
          beepPlayedRef.current = true;
        }
      }
    }, 200);

    if (!open) {
      setWeight(0);
      setDeduct(0);
      setImpurities([{ kind: "", type: "", weight: 0 }]);
      setFocusedImpurityIndex(null);
      setFocusedImpurityField(null);
      setFocusedImpuritySource(null);
      setInputValue(0);
      setNote("");
      setIsEditingNote(false);
      setProdPickerIndex(null);
      setProdPickerSource(null);
      setSnapshots([]);
      impurityWeightRefs.current = {};
      impurityTypeRefs.current = {};
    }

    return () => {
      socket.close();
      clearInterval(interval);
    };
  }, [selectedValue, open, mute]);

  useEffect(() => {
    if (!isEditingPrice) {
      const val = productPrice?.finalPrice;
      setPriceInput(val === undefined || val === null ? "" : String(val));
    }
  }, [productPrice?.finalPrice, isEditingPrice]);

  const calculateNetWeight = (w, d, i) => {
    const weightNum = parseFloat(w || 0);
    const deductNum = parseFloat(d || 0);
    const impuritiesNum = parseFloat(i || 0);
    return weightNum - deductNum - impuritiesNum;
  };

  const totalImpurityWeight = useMemo(() => {
    const normalImpuritiesWeight = impurities.reduce(
      (sum, item) => sum + parseFloat(item.weight || 0),
      0
    );
    const deductedItemsWeight = deductedItems.reduce(
      (sum, item) => sum + parseFloat(item.weight2 || 0),
      0
    );
    return normalImpuritiesWeight + deductedItemsWeight;
  }, [impurities, deductedItems]);

  const netWeight = useMemo(() => {
    return calculateNetWeight(weight, deduct, totalImpurityWeight);
  }, [weight, deduct, totalImpurityWeight]);

  const activeProductId = mainProduct?.id || selectedValue?.id;
  const warehouseStock = Number(
    stockByProductId[activeProductId] ?? productStock ?? 0
  ) || 0;
  const savedSelectedNetWeight =
    selectedValue?.itemId && selectedValue?.id === activeProductId
      ? Number(selectedValue?.netWeight) || 0
      : 0;
  const liveNetWeight = transaction?.status === "waiting"
    ? Number(netWeight) || 0
    : 0;
  const forecastStock = Math.max(
    0,
    (Number(waitingSaleNetByProductId[activeProductId]) || 0) -
      savedSelectedNetWeight +
      liveNetWeight
  );

  const isNetNegative = netWeight < 0;

  const sanitizeNumber = (s) => {
    if (s == null) return "";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, "");
    return x;
  };

  const commitLive = useCallback((nextStr) => {
    const v = sanitizeNumber(nextStr);
    setInputValue(v === "" ? 0 : v);
    setIsFirstKeypress(false);

    if (isEditingNote) return;

    if (focused === "weight") {
      setWeight(v);
    } else if (focused === "deduct") {
      setDeduct(v);
    } else if (focused === "price") {
      syncFinalPrice(v);
    } else if (
      focusedImpurityField === "weight" &&
      focusedImpurityIndex !== null
    ) {
      if (focusedImpuritySource === "impurities") {
        setImpurities((prev) => {
          const arr = [...prev];
          if (!arr[focusedImpurityIndex]) return prev;
          arr[focusedImpurityIndex] = {
            ...arr[focusedImpurityIndex],
            weight: v,
          };
          return arr;
        });
      } else if (focusedImpuritySource === "deducted") {
        const j = focusedImpurityIndex - impurities.length;
        setDeductedItems((prev) => {
          const arr = [...prev];
          if (!arr[j]) return prev;
          arr[j] = { ...arr[j], weight2: v };
          return arr;
        });
      }
    }
  }, [
    isEditingNote,
    focused,
    focusedImpurityField,
    focusedImpurityIndex,
    focusedImpuritySource,
    impurities.length,
    setWeight,
    setDeduct,
    setImpurities,
    setDeductedItems,
  ]);

  useEffect(() => {
    const isWeight = transaction?.isWeighed || false;

    setIsDisabled(isWeight);
    setFocused("weight");
  }, [transaction]);

  useEffect(() => {
    if (focused === null) return;
    if (focused === "weight") setInputValue(weight || 0);
    else if (focused === "deduct") setInputValue(deduct || 0);
    else if (focused === "price") setInputValue(productPrice?.finalPrice ?? 0);
  }, [focused, weight, deduct, productPrice?.finalPrice]);

  useEffect(() => {
    if (!open) return;
    const isTypingInTextInput = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      const isInputLike =
        tag === "input" ||
        tag === "textarea" ||
        el.getAttribute?.("contenteditable") === "true";
      return isInputLike;
    };

    const onKeyDown = (e) => {
      if (isEditingNote) return;
      if (openProdDialog || openMainProdDialog || openPriceDialog) return;
      if (isTypingInTextInput()) return;

      const editing =
        focused === "weight" ||
        focused === "deduct" ||
        focusedImpurityIndex !== null;
      if (!editing) return;

      const key = e.key;

      if (/^[0-9]$/.test(key) || key === ".") {
        e.preventDefault();
        let cur = String(inputValue ?? 0);
        if (isFirstKeypress) {
          cur = "0";
        }
        if (key === "." && cur.includes(".")) return;
        const next =
          cur === "0" && key !== "." ? key : (cur || "") + String(key);
        commitLive(next);
        return;
      }

      if (key === "Backspace") {
        e.preventDefault();
        const cur = String(inputValue ?? 0);
        const next = cur.length <= 1 ? "0" : cur.slice(0, -1);
        commitLive(next);
        return;
      }

      if (key === "Escape") {
        e.preventDefault();
        commitLive("0");
        return;
      }

      if (key === "Enter") {
        e.preventDefault();
        handleKeypadSubmit(inputValue);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    inputValue,
    focused,
    focusedImpurityField,
    focusedImpurityIndex,
    openProdDialog,
    openMainProdDialog,
    openPriceDialog,
    isEditingNote,
    impurities.length,
    focusedImpuritySource,
  ]);

  const handleData = (event) => {
    const data = JSON.parse(event.data);
    if (data.weight != null) {
      const rawWeight = Number(data.weight);
      const roundedWeight = parseFloat(rawWeight.toFixed(3));
      if (
        lastWeightRef.current === null ||
        Math.abs(roundedWeight - lastWeightRef.current) > 0.05
      ) {
        lastWeightRef.current = roundedWeight;
        stableStartTimeRef.current = Date.now();
        beepPlayedRef.current = false;
      }
    }
  };

  const captureImage = useCallback(async () => {
    if (!webcamRef.current) return null;
    const video = webcamRef.current.video;
    if (!video || video.readyState < 2) {
      console.warn("No webcam video available");
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageSrc = canvas.toDataURL("image/jpeg", 1);
    if (!imageSrc) return null;
    const blob = await (await fetch(imageSrc)).blob();
    const file = new File([blob], "webcam_photo.jpg", { type: "image/jpeg" });
    return file;
  }, []);

  async function uploadFile(file) {
    if (!file) throw new Error("No file to upload");
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(SALENG_SHOP_API + "/files", {
      method: "POST",
      headers: getAuthorizationHeaders(),
      body: formData,
    });
    if (!response.ok) throw new Error("Upload failed: " + response.statusText);
    const result = await response.json();
    return result;
  }

  const handleClose = () => {
    setSnapshots([]);
    setWeight("");
    setDeduct("");
    setNote("");
    setImpurities([{ kind: "", type: "", weight: 0 }]);
    onClose();
  };

  const handleSubmit = async () => {
    console.log("snapshots", snapshots);

    try {
      const normalImpurities = impurities.filter(
        (imp) => imp.type !== "__deduct_product__"
      );
      const deductedItemImpurities = impurities.filter(
        (imp) => imp.type === "__deduct_product__"
      );

      // helper: ดึงรูปที่ match กับ item ตาม source + createdAt
      const pickSnapshotsFor = (source, createdAt) => {
        if (!createdAt) return [];
        return (snapshots || [])
          .filter(
            (s) =>
              s?.context?.type === "impurity" &&
              s?.context?.source === source &&
              s?.context?.createdAt === createdAt
          )
          .map((s) => ({
            url: s.url,
            at: s.at,
            label: s.context?.label,
            productId: s.context?.productId,
            productName: s.context?.productName,
            isDeductProduct: !!s.context?.isDeductProduct,
          }));
      };

      // รูปทั่วไปที่ไม่ใช่ของแถวสิ่งเจือปน (พวก weight/deduct/price)
      const generalSnapshots = (snapshots || [])
        .filter(
          (s) =>
            s?.context?.type === "weight" ||
            s?.context?.type === "deduct" ||
            s?.context?.type === "price" ||
            s?.context?.type === "unknown"
        )
        .map((s) => ({ url: s.url, at: s.at, label: s.context?.type }));

      // ====== แปลง “หักเป็นสินค้าอื่น” ที่มาจาก impurities ======
      const dedFromImpurities = deductedItemImpurities
        .map((imp) => {
          const prod = (products || []).find((p) => p.id === imp.productId);
          console.log("prod", prod);
          const w = parseFloat(imp.weight || 0);
          return {
            title: imp.productName,
            productId: imp.productId,
            productName: imp.productName,
            price: prod?.finalPrice,
            weight: w,
            createdAt: imp.createdAt, // เก็บไว้ผูกกับรูป
            source: "impurities", // บอก source ให้ชัด
            attachedImages: pickSnapshotsFor("impurities", imp.createdAt),
            ...imp,
          };
        })
        .filter(Boolean);

      // ====== แปลง “deductedItems” ที่อยู่ใน state อยู่แล้ว ======
      const dedFromState = (deductedItems || []).map((it) => {
        const w = parseFloat(it.weight2 || 0);
        return {
          id: it.id,
          title: it.productName,
          productId: it.productId,
          productName: it.productName,
          price: it.pricePerUnit,
          weight: w,
          createdAt: it.createdAt, // เก็บไว้ผูกกับรูป
          source: "deductedItems", // บอก source ให้ชัด
          attachedImages: pickSnapshotsFor("deductedItems", it.createdAt),
        };
      });

      const dedItems = [...dedFromImpurities, ...dedFromState];

      // ====== แนบรูปให้ normalImpurities ทีละแถว ======
      const normalImpuritiesWithImages = normalImpurities.map((imp) => ({
        ...imp,
        attachedImages: pickSnapshotsFor("impurities", imp.createdAt),
      }));

      const productId = mainProduct?.id || selectedValue?.id;
      const productTitle = mainProduct?.title || selectedValue?.title;
      const categoryId = mainProduct?.categoryId ?? selectedValue?.categoryId;

      const normalImpuritiesWeight = normalImpurities.reduce(
        (s, it) => s + parseFloat(it.weight || 0),
        0
      );
      const dedItemsWeight = dedItems.reduce(
        (s, it) => s + parseFloat(it.weight || 0),
        0
      );
      const netFromSubmit =
        parseFloat(weight || 0) -
        parseFloat(deduct || 0) -
        (normalImpuritiesWeight + dedItemsWeight);

      onSubmit({
        ...selectedValue,
        id: productId,
        title: productTitle,
        categoryId,
        finalPrice: latestFinalPriceRef.current,
        weight,
        deduct,
        netWeight: netFromSubmit,
        impurities: normalImpuritiesWithImages,
        deductedItems: dedItems,
        snapshots,
        generalSnapshots,
        productPrice,
        note,
        isLockedPrice: isLockedPriceState,
      });

      handleClose();
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการส่งข้อมูล:", error);
    }
  };

  const handleKeypadSubmit = async (result) => {
    if (isEditingNote) return;
    if (isCapturingRef.current) return;
    isCapturingRef.current = true;

    try {
      const capturedImage = await captureImage();
      if (capturedImage) {
        const uploadResult = await uploadFile(capturedImage);
        const url = uploadResult?.file;
        if (url) {
          const context = getCurrentSnapshotContext();
          setSnapshots((prev) => [
            ...prev,
            { url, at: new Date().toISOString(), context },
          ]);
        }
      }
    } catch (err) {
      console.log("Error capturing/uploading image:", err);
    } finally {
      isCapturingRef.current = false;
    }

    const safeNum = parseFloat(String(result ?? "0")) || 0;

    if (focused === "weight") {
      if (isDisabled) return;
      setWeight(safeNum);
      if (hasPermission(["edit_deduct_weight"])) {
        setFocused("deduct");
        setIsFirstKeypress(true);
      }
      setInputValue(deduct ?? 0);
    } else if (focused === "deduct") {
      if (isDisabled) return;
      setDeduct(safeNum);
      if (hasPermission(["edit_total_weight"])) {
        setFocused("weight");
        setIsFirstKeypress(true);
      }
      setInputValue(weight ?? 0);
    } else if (focused === "price") {
      syncFinalPrice(safeNum);
      setIsLockedPriceState(true);
      setFocused("weight");
      setIsFirstKeypress(true);
      setInputValue(weight ?? 0);
    } else if (
      focusedImpurityField === "weight" &&
      focusedImpurityIndex != null
    ) {
      if (isDisabled) return;
      const target = mergedItems?.[focusedImpurityIndex];
      if (!target) return;
      const source = target.source === "deducted" ? "deductedItems" : "impurities";
      const key = target.createdAt;

      if (source === "impurities") {
        setImpurities((prev) =>
          prev.map((x) => (x.createdAt === key ? { ...x, weight: safeNum } : x))
        );
      } else {
        setDeductedItems((prev) =>
          prev.map((x) =>
            x.createdAt === key ? { ...x, weight: safeNum, weight2: safeNum } : x
          )
        );
      }
      setInputValue(safeNum);
    }
  };

  const handleSelectPrice = (newPrice) => {
    syncFinalPrice(newPrice.finalPrice);
    setIsLockedPriceState(true);
    setOpenPriceDialog(false);
    setTimeout(() => focusMainWeight(), 0);
  };

  const cancelProductPick = () => {
    setOpenProdDialog(false);
    setSelectedId(null);
    if (prodPickerIndex !== null && prodPickerSource) {
      if (prodPickerSource === "impurities") {
        setImpurities((prev) => {
          const arr = [...prev];
          const row = arr[prodPickerIndex];
          if (row && row.type === "__deduct_product__" && !row.productId) {
            arr[prodPickerIndex] = {
              ...row,
              type: "",
              productId: undefined,
              productName: undefined,
            };
          }
          return arr;
        });
      }
      setProdPickerIndex(null);
      setProdPickerSource(null);
    }
  };

  const handleImpurityChange = (mergedIndex, field, value) => {
    const item = mergedItems[mergedIndex];
    if (!item) return;
    const source = item.source || "impurities"; // แหล่งข้อมูล: impurities หรือ deductedItems

    if (field === "kind") {
      // ถ้าแก้ kind (วิธีหัก)
      if (source === "impurities") {
        setImpurities((prev) =>
          prev.map((x) =>
            x.createdAt === item.createdAt ? { ...x, kind: value, type: "" } : x
          )
        );
      } else {
        setDeductedItems((prev) =>
          prev.map((x) =>
            x.createdAt === item.createdAt ? { ...x, kind: value } : x
          )
        );
      }

      setFocusedImpurityField("type");
      setFocusedImpuritySource(source);
      setTimeout(() => focusImpurityTypeAt(source, item.createdAt), 0);
      return;
    }

    if (field === "type") {
      // ถ้าแก้ type (เลือกสิ่งเจือปน)
      if (source === "impurities") {
        setImpurities((prev) =>
          prev.map((x) =>
            x.createdAt === item.createdAt
              ? {
                ...x,
                type: value,
                kind: x.kind?.trim() || inferKindFromType(value),
              }
              : x
          )
        );

        setTimeout(() => {
          focusImpurityWeightAt(source, mergedIndex); // ต้องแน่ใจว่าใช้ item.createdAt
        }, 0);
      } else if (source === "deducted") {
        setDeductedItems((prev) =>
          prev.map((x) =>
            x.createdAt === item.createdAt
              ? { ...x, type: value, kind: x.kind || inferKindFromType(value) }
              : x
          )
        );

        setTimeout(() => {
          focusImpurityWeightAt(source, mergedIndex); // ต้องแน่ใจว่าใช้ item.createdAt
        }, 0);
      }
    }
  };

  const addImpurity = () => {
    setImpurities((prev) => [
      ...prev,
      { kind: "", type: "", weight: 0, createdAt: new Date() },
    ]);
  };

  const removeImpurityAt = (mergedIndex) => {
    const item = mergedItems[mergedIndex];
    if (!item) return;

    if (item.source === "impurities") {
      setImpurities((prev) =>
        prev.filter((x) => x.createdAt !== item.createdAt)
      );
    } else if (item.source === "deducted") {
      setDeductedItems((prev) =>
        prev.filter((x) => x.createdAt !== item.createdAt)
      );
    }

    setFocusedImpurityIndex(null);
    setFocusedImpurityField(null);
    setFocusedImpuritySource(null);
  };

  const mergedItems = [
    ...impurities.map((item) => ({ source: "impurities", ...item })),
    ...deductedItems.map((item) => ({
      ...item,
      source: "deducted",
      kind: "convert",
      type: "__deduct_product__",
      weight: item.weight2,
      productId: item.productId,
      productName: item.productName,
      pricePerUnit: item.pricePerUnit,
      isDeductProduct: true,
    })),
  ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const resolvePriceFromProduct = (prod) => {
    if (!prod) return 0;
    const num = (v) => Number(v ?? 0) || 0;
    return (
      num(prod.finalPriceWithGroup) ||
      num(prod.finalPrice) ||
      num(prod.price) ||
      num(prod.basePrice) +
      num(prod.branchAdjustment) +
      num(prod.groupAdjustment)
    );
  };

  const getCurrentSnapshotContext = () => {
    // ค่า default
    const base = { type: "unknown" };

    // 1) ช่องหลัก: รวม/หัก/ราคา
    if (focused === "weight") return { type: "weight" };
    if (focused === "deduct") return { type: "deduct" };
    if (focused === "price") return { type: "price" };

    // 2) ช่องสิ่งเจือปน/หักเป็นสินค้าอื่น
    if (focusedImpurityField === "weight" && focusedImpurityIndex != null) {
      // item จาก mergedItems (รวมทั้ง impurities + deductedItems แล้วเรียงตาม createdAt)
      const target = mergedItems?.[focusedImpurityIndex];
      if (!target) return base;

      // normalize ที่มา (จะได้เหลือสองค่าแน่ ๆ)
      const normalizeSource = (s) =>
        s === "deducted" ? "deductedItems" : s || "impurities";
      const source = normalizeSource(target.source);

      const isDeductProduct =
        target.type === "__deduct_product__" || target.isDeductProduct;

      // บอกว่าเป็นแถวไหน (index), มาจากตารางไหน (source), และกุญแจอ้างอิง (createdAt)
      const ctx = {
        type: "impurity",
        field: "weight",
        source, // "impurities" | "deductedItems"
        index: focusedImpurityIndex, // index หลัง merge (ไว้บอกว่า "ช่องไหน")
        createdAt: target.createdAt, // คีย์อ้างอิงเสถียร
        isDeductProduct,
      };

      // ถ้าเป็น “หักเป็นสินค้าอื่น” แนบชื่อ/ไอดีสินค้าไปด้วย
      if (isDeductProduct) {
        ctx.productId = target.productId ?? null;
        ctx.productName = target.productName ?? null;
      }

      // เผื่ออยากโชว์ข้อความอ่านง่ายฝั่งเซิร์ฟเวอร์/UI
      ctx.label = isDeductProduct
        ? `สิ่งเจือปน(หักเป็นสินค้าอื่น) แถวที่ ${focusedImpurityIndex + 1}${ctx.productName ? ` - ${ctx.productName}` : ""}`
        : `สิ่งเจือปน แถวที่ ${focusedImpurityIndex + 1}${target.type ? ` - ${target.type}` : ""}`;

      return ctx;
    }

    return base;
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Custom Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-blue-50/50 border border-blue-100/50" />
            <span className="text-[28px] font-black text-slate-800 tracking-tight">
              {mainProduct?.title || selectedValue?.title}
            </span>
            <button
              onClick={() => setOpenMainProdDialog(true)}
              className="text-slate-300 hover:text-blue-500 transition-colors"
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </button>
          </div>

          {hasPermission(["show_price_in_ticket"]) && (
            <div
              onClick={() => {
                setFocused("price");
                setInputValue(productPrice?.finalPrice ?? 0);
                setIsFirstKeypress(true);
              }}
              className={`flex items-center gap-3 py-1 px-4 rounded-full transition-all cursor-pointer ${focused === "price" ? 'bg-blue-50 ring-2 ring-blue-400' : 'hover:bg-slate-50'}`}
            >
              <span className="text-[14px] font-bold text-slate-400 uppercase tracking-widest">ราคาต่อหน่วย</span>
              <div className="flex items-center gap-3">
                <span className="text-[32px] font-black text-blue-600 tracking-tighter">
                  {formatNumber(productPrice?.finalPrice ?? 0)}
                </span>
                {/* <FormatListBulletedIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPriceDialog(true);
                  }}
                  sx={{ fontSize: 24, color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
                /> */}
              </div>
            </div>
          )}

          <IconButton onClick={handleClose} className="text-slate-400 hover:text-slate-600">
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent sx={{ p: 4, bgcolor: '#fff', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div className="grid grid-cols-12 gap-8 h-full min-h-0">

            {/* Left Column: Webcam & Notes */}
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-3 h-full min-h-0 overflow-y-auto pb-2 pr-2 customize-scrollbar">
              <div className="relative shrink-0 aspect-[4/3] bg-black rounded-[20px] overflow-hidden border-4 border-slate-50 shadow-sm">
                <Webcam
                  ref={webcamRef}
                  key={selectedDeviceId}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={0.9}
                  className="w-full h-full object-cover"
                  videoConstraints={{
                    width: 1920,
                    height: 1080,
                    deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
                  }}
                />

                {/* Camera Selector (Overlay) */}
                <div className="absolute top-4 left-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur rounded-xl border border-white/20 shadow-sm overflow-hidden">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                  <select
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                    className="bg-transparent text-[13px] font-bold text-slate-700 outline-none w-full truncate"
                  >
                    {devices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `กล้อง ${d.deviceId}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* REC Indicator */}
                <div className="absolute bottom-4 left-4 flex items-center gap-2 px-2 py-1 bg-black/40 backdrop-blur rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-rose-500" />
                  <span className="text-[9px] font-black text-white uppercase tracking-widest">REC</span>
                </div>
              </div>

              <div className="shrink-0 rounded-[20px] border border-slate-100 bg-white px-5 py-3 shadow-sm">
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[16px] font-bold text-slate-500">สุทธิส่งขาย</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-[22px] font-black text-slate-800">
                        {formatNumber(netWeight)}
                      </span>
                      <span className="text-[14px] font-bold text-slate-400">กก.</span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="text-[16px] font-bold text-slate-500">สต๊อกย่อยคาดการณ์</div>
                      <div className="text-[11px] font-medium text-slate-400">
                        รวมสินค้าชนิดเดียวกันในตั๋วรอขายทั้งหมด
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-[22px] font-black ${forecastStock < 0 ? "text-rose-500" : "text-slate-800"}`}>
                        {formatNumber(forecastStock)}
                      </span>
                      <span className="text-[14px] font-bold text-slate-400">กก.</span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[16px] font-bold text-slate-500">สต๊อกย่อยในคลัง</span>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-[22px] font-black ${warehouseStock < forecastStock ? "text-rose-500" : "text-slate-800"}`}>
                        {formatNumber(warehouseStock)}
                      </span>
                      <span className="text-[14px] font-bold text-slate-400">กก.</span>
                    </div>
                  </div>
                </div>
              </div>

              <textarea
                placeholder="หมายเหตุ..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onFocus={() => setIsEditingNote(true)}
                onBlur={() => setIsEditingNote(false)}
                className="w-full h-20 shrink-0 bg-slate-50/50 border border-slate-100 rounded-[20px] p-4 text-slate-600 placeholder:text-slate-300 outline-none focus:border-blue-100 focus:bg-white transition-all resize-none text-sm font-medium"
              />
            </div>

            {/* Middle Column: Weights & Summary */}
            <div className="col-span-12 lg:col-span-5 flex flex-col h-full min-h-0">
              <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-4 customize-scrollbar">
                {/* Total Weight Card */}
                <div
                  onClick={() => {
                    setFocused("weight");
                    setInputValue(weight || 0);
                    setIsFirstKeypress(true);
                  }}
                  className={`relative py-[10px] px-6 rounded-[24px] border-2 transition-all cursor-pointer bg-white ${focused === "weight" ? 'border-emerald-500 ring-4 ring-emerald-500/5' : 'border-slate-100'}`}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <ScaleIcon className="text-emerald-500" />

                    <span className="text-[17px] font-black text-emerald-600 uppercase tracking-tight">น้ำหนักชั่ง (รวม)</span>
                    <div className="ml-auto bg-emerald-500 text-white text-[10px] font-black px-2 py-1 rounded-md tracking-widest uppercase shadow-sm shadow-emerald-200">AUTO</div>
                  </div>
                  <div className="flex items-baseline justify-end gap-3 pr-2">
                    <span className="text-[48px] font-black text-slate-800 leading-none tracking-tighter">
                      {formatNumber(weight)}
                    </span>
                    <span className="text-slate-400 font-black text-2xl">กก.</span>
                  </div>
                </div>

                {/* Deduct Weight Card */}
                <div
                  onClick={() => {
                    setFocused("deduct");
                    setInputValue(deduct || 0);
                    setIsFirstKeypress(true);
                  }}
                  className={`relative py-[10px] px-6 rounded-[24px] border-2 transition-all cursor-pointer bg-white ${focused === "deduct" ? 'border-rose-400 ring-4 ring-rose-500/5' : 'border-slate-100'}`}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <ScaleIcon className="text-emerald-500" />

                    <span className="text-[17px] font-black text-rose-600 uppercase tracking-tight">น้ำหนักชั่ง (หัก)</span>
                  </div>
                  <div className="flex items-baseline justify-end gap-3 pr-2">
                    <span className="text-[48px] font-black text-rose-500 leading-none tracking-tighter">
                      {formatNumber(deduct)}
                    </span>
                    <span className="text-slate-400 font-black text-2xl">กก.</span>
                  </div>
                </div>

                {/* Impurity List */}
                <div className="space-y-3">
                  {mergedItems.map((item, index) => {
                    const source = item.source || "impurities";
                    const isFocused = focusedImpurityIndex === index;
                    const isDeductProduct = item.type === "__deduct_product__" || item.isDeductProduct;

                    return (
                      <div
                        key={`${source}-${item.createdAt}`}
                        className={`group relative py-5 px-4 rounded-[20px] border-2 transition-all bg-white ${isFocused ? 'border-blue-400 shadow-sm' : 'border-slate-50 hover:border-slate-100'}`}
                      >
                        <div className="grid grid-cols-12 gap-3 items-center">
                          {/* Kind Selector */}
                          <div className="col-span-3">
                            <select
                              value={item.kind || ""}
                              onChange={(e) => handleImpurityChange(index, "kind", e.target.value)}
                              className="w-full h-11 bg-slate-50 border-none rounded-xl px-2 py-3 text-[13px] font-bold text-slate-700 focus:ring-2 focus:ring-blue-100 outline-none appearance-none cursor-pointer"
                            >
                              <option value="">วิธีหัก</option>
                              {deductKindOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Type/Product Selector */}
                          <div className="col-span-5">
                            {item.kind === "convert" ? (
                              <button
                                onClick={() => {
                                  setProdPickerIndex(index);
                                  setProdPickerSource(source);
                                  setOpenProdDialog(true);
                                }}
                                className="w-full h-11 px-3 bg-blue-50/50 rounded-xl text-[13px] font-bold text-blue-600 hover:bg-blue-50 transition-colors text-left truncate border border-blue-100/50"
                              >
                                {item.productName || "เลือกสินค้า..."}
                              </button>
                            ) : (
                              <select
                                value={item.type || ""}
                                onChange={(e) => handleImpurityChange(index, "type", e.target.value)}
                                className="w-full h-11 bg-slate-50 border-none rounded-xl px-2 py-3 text-[13px] font-bold text-slate-700 focus:ring-2 focus:ring-blue-100 outline-none appearance-none cursor-pointer"
                              >
                                <option value="">เลือกสิ่งเจือปน</option>
                                {getTypeOptionsByKind(item.kind).map((type, i) => (
                                  <option key={i} value={type}>{type}</option>
                                ))}
                              </select>
                            )}
                          </div>

                          {/* Weight Input Display */}
                          <div
                            onClick={() => {
                              setFocused(null);
                              setFocusedImpurityIndex(index);
                              setFocusedImpurityField("weight");
                              setFocusedImpuritySource(source);
                              setInputValue(item.weight || 0);
                              setIsFirstKeypress(true);
                            }}
                            className={`col-span-3 h-11 flex items-center justify-center rounded-xl border-2 transition-all cursor-pointer ${isFocused && focusedImpurityField === "weight" ? 'border-blue-500 ring-2 ring-blue-100 bg-white' : 'border-slate-100 bg-white hover:border-blue-100'}`}
                          >
                            <span className={`text-[19px] font-black ${isFocused && focusedImpurityField === "weight" ? 'text-blue-600' : 'text-slate-700'}`}>
                              {item.weight === 0 && isFocused && focusedImpurityField === "weight" ? "0" : formatNumber(item.weight)}
                            </span>
                          </div>

                          {/* Delete Button */}
                          <div className="col-span-1 flex justify-end">
                            <button
                              onClick={() => removeImpurityAt(index)}
                              className="w-10 h-10 flex items-center justify-center bg-rose-500 text-white rounded-xl hover:bg-rose-600 shadow-sm shadow-rose-100 transition-all"
                            >
                              <CloseIcon sx={{ fontSize: 20 }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Impurity Button */}
                <button
                  onClick={addImpurity}
                  disabled={!hasPermission(["edit_impurity"])}
                  className="w-full py-1 border-2 border-dashed border-slate-100 rounded-[32px] flex items-center justify-center gap-3 text-slate-400 hover:text-blue-500 hover:border-blue-200 hover:bg-blue-50/10 transition-all font-black group shrink-0"
                >
                  <div className="p-1 px-1.5 rounded-full border-2 border-slate-200 group-hover:border-blue-500 group-hover:text-blue-500 transition-colors">
                    <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
                  </div>
                  <span className="text-lg">เพิ่มสิ่งเจือปน</span>
                </button>
              </div>

              {/* Summary Section */}
              <div className="pt-6 border-t border-slate-50 flex flex-col gap-3 shrink-0">
                <div className="flex justify-between items-baseline">
                  <span className="text-[19px] font-black text-slate-500 tracking-tight">น้ำหนักสุทธิ</span>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-3xl font-black tracking-tighter ${isNetNegative ? 'text-rose-500' : 'text-slate-800'}`}>
                      {formatNumber(netWeight)}
                    </span>
                    <span className="text-slate-400 font-black text-xl">กก.</span>
                  </div>
                </div>
                {hasPermission(["show_price_in_ticket"]) && (
                  <div className="flex justify-between items-baseline">
                    <span className="text-[19px] font-black text-slate-500 tracking-tight">รวมราคา</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-black text-emerald-500 tracking-tighter leading-none">
                        {formatNumber(netWeight * (productPrice?.finalPrice || 0))}
                      </span>
                      <span className="text-emerald-500 font-black text-3xl">฿</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Keypad */}
            <div className="col-span-12 lg:col-span-3 flex flex-col h-full min-h-0">
              <div className="bg-white border border-slate-100 rounded-[24px] overflow-hidden shadow-sm flex-1 flex flex-col">
                <div className="flex-1 p-2 min-h-0">
                  <Keypad
                    value={inputValue}
                    onChange={(val) => {
                      if (isEditingNote) return;
                      setInputValue(val);
                      commitLive(val);
                    }}
                    onClear={() => {
                      if (isEditingNote) return;
                      setInputValue(0);
                      if (focused === "weight") setWeight(0);
                      if (focused === "deduct") setDeduct(0);
                      if (focused === "price") syncFinalPrice(0);
                    }}
                    onSubmit={handleKeypadSubmit}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ p: 0, flexShrink: 0, position: "relative", zIndex: 1 }}>
          <button
            onClick={handleSubmit}
            className="w-full py-5 bg-emerald-500 text-white text-xl font-black flex items-center justify-center gap-3 hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-50"
          >
            <div className="w-8 h-8 rounded-full border-4 border-white/30 flex items-center justify-center">
              <CheckIcon sx={{ fontSize: 20 }} />
            </div>
            ยืนยัน
          </button>
        </DialogActions>
      </Dialog>

      {/* TODO */}
      <ProductSelectDialog
        open={openProdDialog}
        onClose={cancelProductPick}
        onConfirm={(prod) => {
          if (!prod) return cancelProductPick();
          const mergedIndex = prodPickerIndex;
          if (mergedIndex == null) return cancelProductPick();

          // 1) ชี้เป้าหมายจาก mergedItems ตามจริง
          const target = mergedItems?.[mergedIndex];
          if (!target) return cancelProductPick();

          // บางที่เก็บเป็น "deducted" ให้ normalize เป็น "deductedItems"
          const normalizeSource = (s) =>
            s === "deducted" ? "deductedItems" : s;
          const source = normalizeSource(target.source || "impurities");
          const createdKey = target.createdAt;
          if (!createdKey) return cancelProductPick();

          // 2) ของที่จะทับค่า (คงน้ำหนักเดิม + default kind=convert)
          const applyOverride = (prev) => ({
            ...prev,
            kind: prev?.kind || "convert",
            type: "__deduct_product__",
            productId: prod.id,
            productName: prod.title ?? prod.name ?? "",
            weight: Number(prev?.weight ?? 0),
          });

          // 3) อัปเดตด้วย createdAt (ไม่ใช้ index อีกแล้ว)
          if (source === "impurities") {
            setImpurities((prev) =>
              prev.map((x) =>
                x.createdAt === createdKey ? applyOverride(x) : x
              )
            );
          } else if (source === "deductedItems") {
            setDeductedItems((prev) =>
              prev.map((x) =>
                x.createdAt === createdKey ? applyOverride(x) : x
              )
            );
          }

          // 4) โฟกัสช่องน้ำหนักของรายการนั้นด้วย createdAt
          setTimeout(() => {
            focusImpurityWeightAt(source, mergedIndex);
          }, 0);

          // 5) ปิด dialog + ล้าง state
          setOpenProdDialog(false);
          setProdPickerIndex(null);
          setProdPickerSource(null);
        }}
        products={products}
        categories={categories}
      />

      <ProductSelectDialog
        open={openMainProdDialog}
        onClose={() => setOpenMainProdDialog(false)}
        onConfirm={(prod) => {
          setMainProduct({
            id: prod.id,
            title: prod.title,
            categoryId: prod.category?.id,
          });

          // ตั้งราคาให้ตรงกับสินค้าใหม่ทันที
          const newFinal = resolvePriceFromProduct(prod);
          latestFinalPriceRef.current = newFinal;
          setProductPrice((prev) => ({
            ...(prev || {}),
            id: prod.id,
            finalPrice: newFinal,
          }));
          setIsLockedPriceState(false);

          setOpenMainProdDialog(false);
          setTimeout(() => focusMainWeight(), 0);
        }}
        products={products}
        categories={categories}
        initialSelectedId={mainProduct?.id || selectedValue?.id}
      />

      <PriceSelectionDialog
        open={openPriceDialog}
        onClose={() => setOpenPriceDialog(false)}
        onSelect={handleSelectPrice}
        categoryId={mainProduct?.categoryId || selectedValue?.categoryId}
        productId={productPrice?.id}
        products={products}
        transaction={transaction}
      />
    </>
  );
};

export default SimpleDialog;
